import { App as CapacitorApp, URLOpenListenerEvent } from '@capacitor/app';
import { InAppBrowser } from '@capgo/inappbrowser';
import { captureException } from '@sentry/react';
import { AnimatePresence } from 'framer-motion';
import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';

import { ConditionalWrap } from '@frontend-libs/atomic';

import { PageViewTracker } from './analytics';
import { AppLayout, BottomNavbarProvider, ToastProvider } from './components';
import { useLicenses } from './licenses';
import { OnboardingProvider } from './modules/onboarding';
import { PushNotificationsProvider } from './notifications';
import { usePortalPublicConfiguration } from './portal-public-configuration';
import {
  privateLandingNavigation,
  publicLandingNavigation,
  RoutesImporterState,
  routes,
  ShopProvider,
  useOptionalPortalConfiguration,
  usePortalConfiguration,
} from './utils';
import { PageNotFoundView } from './views';

export interface AppRoutesProps {
  validSession: boolean;
  // We do Partial first so Required can properly remove the
  // undefined of the routes
  routes: Required<Partial<RoutesImporterState>>['routes'];
}

export const AppRoutes = (props: AppRoutesProps) => {
  const publicConfig = usePortalPublicConfiguration();
  const privateConfig = useOptionalPortalConfiguration();

  const navigate = useNavigate();

  const portalConfig = useMemo(() => {
    if (props.validSession) {
      return privateConfig;
    }

    return publicConfig;
  }, [props.validSession, publicConfig, privateConfig]);

  const {
    news: newsLicense,
    vouchers: vouchersLicense,
    stampCards: stampCardsLicense,
  } = useLicenses();

  useEffect(() => {
    CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      if (event.url) {
        const url = new URL(event.url);
        let route: string;

        if (url.protocol !== 'https:' && url.protocol !== 'http:') {
          route = `/${url.host}${url.pathname}${url.search}`;
        } else {
          route = url.pathname + url.search;
        }

        navigate(route);
      }
    });

    // Listen to URL changes in the in-app browser and route
    // them in the app if they are pointing to the app domain.
    // This is needed for example for handling redirects
    // from 3rd party payment providers back to the app.
    const inAppBrowserUrlListener = InAppBrowser.addListener(
      'urlChangeEvent',
      (event) => {
        if (!event.url) {
          return;
        }
        const url = new URL(event.url);
        const appOrigin = new URL(APP_CONFIG.baseUrl || window.location.origin)
          .origin;

        if (url.origin !== appOrigin) {
          return;
        }

        let returnUrl = '';
        const startIndexOfReturnUrl = url.search.indexOf('returnUrl=');

        if (startIndexOfReturnUrl !== -1) {
          const encodedReturnUrl = url.search.slice(
            startIndexOfReturnUrl + 'returnUrl='.length,
          );
          const decoded = decodeURIComponent(encodedReturnUrl);
          // base64 encode to ensure all params are preserved
          returnUrl = btoa(decoded);
          url.searchParams.set('returnUrl', returnUrl);
        }

        const route = `${url.pathname}${url.search}`;
        navigate(route);
      },
    );

    return () => {
      inAppBrowserUrlListener
        .then((handle) => handle.remove())
        .catch((error) => {
          console.error(error);
          captureException(error);
        });
      CapacitorApp.removeAllListeners();
    };
  }, []);

  return (
    <ConditionalWrap
      condition={props.validSession}
      wrap={(children) => {
        return (
          <PushNotificationsProvider>{children}</PushNotificationsProvider>
        );
      }}
    >
      <AnimatePresence>
        <PageViewTracker key="page-tracker" />
        <Routes key="routes">
          <Route
            element={
              <ToastProvider>
                <AppLayout>
                  <OnboardingProvider>
                    <BottomNavbarProvider>
                      <Outlet />
                    </BottomNavbarProvider>
                  </OnboardingProvider>
                </AppLayout>
              </ToastProvider>
            }
          >
            <Route path="/sites/*" element={<props.routes.SiteUnitRoutes />} />

            {portalConfig?.features?.news?.enabled && newsLicense && (
              <Route path="/news/*" element={<props.routes.NewsRoutes />} />
            )}

            {portalConfig?.features?.home?.enabled && (
              <Route path="/home/*" element={<props.routes.HomeRoutes />} />
            )}

            {!props.validSession && (
              <>
                <Route
                  path={routes.landing}
                  element={
                    <Navigate
                      to={publicLandingNavigation(
                        publicConfig?.features?.home?.enabled ?? false,
                      )}
                    />
                  }
                />

                <Route
                  path={routes.welcome}
                  element={<props.routes.WelcomeView />}
                />

                <Route
                  path={routes.auth.login}
                  element={<props.routes.LoginView />}
                />
                {(publicConfig.features?.anonymousSelfSignUp?.enabled ||
                  publicConfig.features?.existingSelfSignUp?.enabled) && (
                  <Route
                    path={routes.auth.signUp}
                    element={<props.routes.SignupView />}
                  />
                )}
                <Route
                  path={`${routes.auth.forgotPassword}/:passwordSessionToken?`}
                  element={<props.routes.ForgotPasswordView />}
                />
                <Route
                  path={`${routes.auth.verifyEmail}/:verifyEmailToken?`}
                  element={<props.routes.VerifyEmailView />}
                />

                <Route path="*" element={<Navigate to={routes.landing} />} />
              </>
            )}

            {props.validSession && (
              <>
                <Route
                  element={
                    <ShopProvider>
                      <Outlet />
                    </ShopProvider>
                  }
                >
                  <Route
                    path={routes.landing}
                    element={
                      <Navigate
                        to={privateLandingNavigation(
                          privateConfig?.loginLandingPage,
                          privateConfig?.features.home?.enabled ?? false,
                        )}
                      />
                    }
                  />

                  <Route
                    path="/wallet/*"
                    element={<props.routes.WalletRoutes />}
                  />
                  <Route path="/not-found" element={<PageNotFoundView />} />

                  <Route
                    path="/profile/*"
                    element={<props.routes.ProfileRoutes />}
                  />

                  {props.validSession &&
                    LoggedInRoutes(
                      props.routes,
                      vouchersLicense,
                      stampCardsLicense,
                    )}

                  <Route
                    path={`${routes.auth.verifyEmail}/:verifyEmailToken?`}
                    element={<props.routes.VerifyEmailView />}
                  />

                  <Route
                    path={routes.welcome}
                    element={<Navigate to={routes.landing} />}
                  />

                  <Route
                    path="/*"
                    element={<props.routes.PageNotFoundView />}
                  />
                </Route>

                <>
                  {/*
                   * If the user is already logged in but using an old auth URL they should be
                   * properly redirected to the landing page
                   */}
                  <Route
                    path={routes.auth.login}
                    element={<Navigate to={routes.landing} />}
                  />
                  <Route
                    path={`${routes.auth.forgotPassword}/*`}
                    element={<Navigate to={routes.landing} />}
                  />
                  {(publicConfig.features?.anonymousSelfSignUp?.enabled ||
                    publicConfig.features?.existingSelfSignUp?.enabled) && (
                    <Route
                      path={routes.auth.signUp}
                      element={<Navigate to={routes.landing} />}
                    />
                  )}
                </>
              </>
            )}
            <Route path="/unauth" element={<props.routes.UnAuthView />} />
            <Route path="*" element={<props.routes.PageNotFoundView />} />
          </Route>
        </Routes>
      </AnimatePresence>
    </ConditionalWrap>
  );
};

const LoggedInRoutes = (
  routes: Required<Partial<RoutesImporterState>>['routes'],
  vouchersLicense: boolean | undefined,
  stampCardsLicense: boolean | undefined,
) => {
  const configuration = usePortalConfiguration();

  return (
    <>
      {configuration?.config?.features?.topup?.enabled && (
        <>
          <Route path="/topup/*" element={<routes.TopupRoutes />} />
          <Route path="/payment/*" element={<routes.PaymentRoutes />} />
        </>
      )}
      {((vouchersLicense &&
        configuration?.config?.features?.voucher?.enabled) ||
        (stampCardsLicense &&
          configuration?.config?.features?.stampCard?.enabled) ||
        configuration?.config?.features?.rule?.enabled) && (
        <Route path="/benefits/*" element={<routes.BenefitsRoutes />} />
      )}
    </>
  );
};
